import Button from "@imminently/decisively-ui/Button";
import DateInput from "@imminently/decisively-ui/DateInput";
import DateTimeField from "@imminently/decisively-ui/DateTimeField";
import DateTimePicker from "@imminently/decisively-ui/DateTimePicker";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import Select from "@imminently/decisively-ui/Select";
import Switch from "@imminently/decisively-ui/Switch";
import { formatDate, getLocaleDateOrder, parseDate } from "@imminently/decisively-ui/dateTimeFormat";
import { addDays, format } from "date-fns";
import * as React from "react";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage";
import {
  DateTimeTestInputs,
  KeyboardReference,
  LOCALE_OPTIONS,
  type TestInput,
  resolveLocale,
} from "../components/DateTimeTestInputs";

const DATE_TEST_INPUTS: TestInput[] = [
  { input: "25/12/2026", expect: "accept", note: "day-first locales" },
  { input: "25-12-2026", expect: "accept", note: "day-first locales" },
  { input: "2026-12-25", expect: "accept" },
  { input: "25 Dec 2026", expect: "accept" },
  { input: "31/06/2026", expect: "reject" },
  { input: "01/13/2026", expect: "reject", note: "day-first locales" },
];

const KEYBOARD_ROWS: [string, string][] = [
  ["Type + Enter / Tab", "Commit the typed date. Invalid text stays visible and is marked invalid."],
  ["ArrowDown", "Open the calendar with focus on the selected (or today's) day."],
  ["Arrow keys", "Move between days in the calendar."],
  ["PageUp / PageDown", "Previous / next month (add Shift for year)."],
  ["Enter / Space", "Pick the focused day, close, and return focus to the input."],
  ["Escape", "Close the calendar and return focus to the input."],
];

const formatValue = (value: Date | null) => (value ? format(value, "yyyy-MM-dd") : "null");

export function DatePickerPage() {
  const [value, setValue] = React.useState<Date | null>(null);
  const [locale, setLocale] = React.useState("browser");
  const [clearable, setClearable] = React.useState(true);
  const [allowNow, setAllowNow] = React.useState(false);
  const [now, setNow] = React.useState(false);
  const [limitRange, setLimitRange] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [fullWidth, setFullWidth] = React.useState(false);
  const [pickerValue, setPickerValue] = React.useState<Date | null>(null);

  const today = React.useMemo(() => new Date(), []);
  const order = getLocaleDateOrder(resolveLocale(locale));

  return (
    <ComponentPage
      title="Date Picker"
      description="DateInput: a typed date with a calendar popover. DateTimeField with dateOnly wraps it for forms."
    >
      <ComponentSection
        title="Playground"
        description="Type a date or open the calendar. Typed text commits on blur or Enter."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview className="flex-col items-stretch gap-2">
            <DateInput
              aria-label="Date"
              value={value}
              onValueChange={setValue}
              locale={resolveLocale(locale)}
              clearable={clearable}
              allowNow={allowNow}
              now={now}
              onNowChange={setNow}
              minDate={limitRange ? today : undefined}
              maxDate={limitRange ? addDays(today, 30) : undefined}
              disabled={disabled}
              error={error}
              fullWidth={fullWidth}
            />
            <code className="text-xs text-muted-foreground">{now ? "now" : formatValue(value)}</code>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Locale">
              <Select
                value={locale}
                onValueChange={(next) => setLocale(next ?? "browser")}
                options={LOCALE_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Clearable">
              <Switch
                checked={clearable}
                onCheckedChange={setClearable}
              />
            </ControlRow>
            <ControlRow label="Allow now">
              <Switch
                checked={allowNow}
                onCheckedChange={setAllowNow}
              />
            </ControlRow>
            <ControlRow label="Next 30 days only">
              <Switch
                checked={limitRange}
                onCheckedChange={setLimitRange}
              />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch
                checked={disabled}
                onCheckedChange={setDisabled}
              />
            </ControlRow>
            <ControlRow label="Error">
              <Switch
                checked={error}
                onCheckedChange={setError}
              />
            </ControlRow>
            <ControlRow label="Full width">
              <Switch
                checked={fullWidth}
                onCheckedChange={setFullWidth}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      <ComponentSection
        title="Keyboard"
        description="The calendar is react-day-picker, which provides the grid navigation."
      >
        <KeyboardReference rows={KEYBOARD_ROWS} />
      </ComponentSection>

      <ComponentSection title="States">
        <ComponentPreview>
          <VariantGrid columns={4}>
            <VariantItem label="Empty">
              <DateInput aria-label="Empty" />
            </VariantItem>
            <VariantItem label="With value">
              <DateInput
                aria-label="With value"
                value={new Date(2026, 11, 25)}
              />
            </VariantItem>
            <VariantItem label="Disabled">
              <DateInput
                aria-label="Disabled"
                value={new Date(2026, 11, 25)}
                disabled
              />
            </VariantItem>
            <VariantItem label="Error">
              <DateInput
                aria-label="Error"
                value={new Date(2026, 11, 25)}
                error
              />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Form field"
        description="DateTimeField with dateOnly. Stores yyyy-MM-dd strings by default; serialize changes the stored type."
      >
        <ComponentPreview className="items-stretch justify-start">
          <Form
            className="grid w-full max-w-xl gap-4"
            defaultValues={{ dueDate: "2026-12-25", expiry: new Date(2026, 11, 25).getTime() }}
          >
            <>
              <DateTimeField
                name="dueDate"
                label="Due date"
                helperText="Default storage: yyyy-MM-dd string."
                dateOnly
                fullWidth
              />
              <DateTimeField<number>
                name="expiry"
                label="Expiry"
                helperText="serialize to epoch milliseconds, as the API key modal does."
                serialize={{
                  to: (date) => date?.getTime() ?? 0,
                  from: (stored) => (stored ? new Date(stored) : null),
                }}
                dateOnly
                fullWidth
              />
              <FieldWatch names={["dueDate", "expiry"]}>
                {(values) => <code className="text-xs text-muted-foreground">{JSON.stringify(values)}</code>}
              </FieldWatch>
            </>
          </Form>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Custom trigger"
        description="DateTimePicker with dateOnly opens the same calendar from any trigger, without a text input."
      >
        <ComponentPreview className="flex-col items-start gap-2">
          <DateTimePicker
            dateOnly
            value={pickerValue}
            onValueChange={setPickerValue}
          >
            <DateTimePicker.Trigger asChild>
              <Button
                variant="outline"
                label="Choose date"
              >
                {pickerValue ? formatDate(pickerValue, order) : "Choose date"}
              </Button>
            </DateTimePicker.Trigger>
            <DateTimePicker.Content />
          </DateTimePicker>
          <code className="text-xs text-muted-foreground">{formatValue(pickerValue)}</code>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Test inputs">
        <DateTimeTestInputs
          description={`From the ENG-240 risk check. Parsed with the locale selected in the playground (${order}).`}
          inputs={DATE_TEST_INPUTS}
          describe={(input) => {
            const parsed = parseDate(input, order);
            return parsed ? format(parsed, "EEE d MMM yyyy") : null;
          }}
        />
      </ComponentSection>
    </ComponentPage>
  );
}
